import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import T2TVendorForm from "./T2TVendorForm";
import T2TVendorFormIntl from "./T2TVendorFormIntl";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5500/api";

// ---- Portal-specific fetch (token auth, not JWT) ----
const portalFetch = async (endpoint, method = "GET", body = null, token = "") => {
  const url = `${API_BASE}/${endpoint}${endpoint.includes("?") ? "&" : "?"}token=${token}`;
  const opts = { method, headers: { "Content-Type": "application/json", "x-vendor-token": token } };
  if (body) opts.body = JSON.stringify(body);
  try {
    const resp = await fetch(url, opts);
    const data = await resp.json();
    return data.status === 200 || resp.ok ? { data } : { error: data };
  } catch (_e) {
    return { error: { message: "Network error. Please try again." } };
  }
};

const PORTAL_STEPS = [
  { id: 1, label: "General Info" },
  { id: 2, label: "Address" },
  { id: 3, label: "Tax & Compliance" },
  { id: 4, label: "Banking" },
  { id: 5, label: "Partner Functions" },
  { id: 6, label: "Documents" },
];

/**
 * T2TVendorPortal \u2014 External vendor self-service form.
 * No OSIF login required \u2014 token-based auth from URL.
 * Route: /vendor-portal?token=XXXXX
 */
const T2TVendorPortal = () => {
  // Extract token from URL
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token") || "";

  const [state, setState] = useState("loading"); // loading | invalid | form | submitted | status
  const [invitation, setInvitation] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const autoSaveRef = useRef(null);

  const isDomestic = invitation?.account_group?.startsWith("Z1");

  // ---- Validate token on mount ----
  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    const validate = async () => {
      const resp = await portalFetch("vendor-portal/validate-token", "GET", null, token);
      if (resp.error) { setState("invalid"); return; }
      const inv = resp.data?.data;
      setInvitation(inv);
      setFormData((prev) => ({
        ...prev,
        account_group: inv?.account_group || "",
        vendor_name: inv?.vendor_name || "",
      }));
      // Load draft if exists
      const draftResp = await portalFetch("vendor-portal/draft", "GET", null, token);
      if (!draftResp.error && draftResp.data?.data) {
        const draft = draftResp.data.data;
        if (draft.form_data && typeof draft.form_data === "object") {
          setFormData((prev) => ({ ...prev, ...draft.form_data }));
        }
        if (draft.current_step) setCurrentStep(draft.current_step);
      }
      setState("form");
    };
    validate();
  }, [token]);

  // ---- Auto-save every 30 seconds ----
  useEffect(() => {
    if (state !== "form") return;
    autoSaveRef.current = setInterval(() => {
      saveDraft(true);
    }, 30000);
    return () => { if (autoSaveRef.current) clearInterval(autoSaveRef.current); };
  }, [state, formData, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = useCallback(async (silent = false) => {
    if (!token || state !== "form") return;
    if (!silent) setSaving(true);
    await portalFetch("vendor-portal/draft", "POST", { form_data: formData, current_step: currentStep }, token);
    setLastSaved(new Date());
    if (!silent) setSaving(false);
  }, [token, state, formData, currentStep]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  }, [errors]);

  // ---- Validation per step ----
  const validateStep = (step) => {
    const errs = {};
    if (step === 1) {
      if (!formData.vendor_name?.trim()) errs.vendor_name = "Vendor name is required.";
      if (!formData.account_group) errs.account_group = "Account group is required.";
      if (!formData.vendor_category) errs.vendor_category = "Category is required.";
    }
    if (step === 2) {
      if (!formData.street_address?.trim()) errs.street_address = "Street address is required.";
      if (!formData.city?.trim()) errs.city = "City is required.";
      if (isDomestic && !formData.state_code) errs.state_code = "State is required.";
      if (!formData.postal_code?.trim()) errs.postal_code = "PIN / Postal code is required.";
      if (!formData.phone_number?.trim()) errs.phone_number = "Phone number is required.";
    }
    if (step === 3) {
      if (isDomestic && !formData.pan_number?.trim()) errs.pan_number = "PAN is required.";
      if (isDomestic && formData.account_group !== "Z103" && formData.account_group !== "Z105" && !formData.gstin?.trim()) errs.gstin = "GSTIN is required.";
      if (!isDomestic && !formData.country_of_origin) errs.country_of_origin = "Country is required.";
    }
    if (step === 4) {
      if (isDomestic) {
        if (!formData.bank_name?.trim()) errs.bank_name = "Bank name is required.";
        if (!formData.bank_account_number?.trim()) errs.bank_account_number = "Account number is required.";
        if (!formData.ifsc_code?.trim()) errs.ifsc_code = "IFSC code is required.";
      } else {
        if (!formData.intl_bank_name?.trim()) errs.intl_bank_name = "Bank name is required.";
        if (!formData.swift_bic?.trim()) errs.swift_bic = "SWIFT code is required.";
        if (!formData.intl_account_number?.trim()) errs.intl_account_number = "Account number is required.";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    if (validateStep(currentStep)) {
      saveDraft(true);
      setCurrentStep((prev) => Math.min(prev + 1, 6));
    }
  };

  const goBack = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // ---- Submit ----
  const handleSubmit = async () => {
    // Validate all steps
    for (let s = 1; s <= 4; s++) {
      if (!validateStep(s)) { setCurrentStep(s); return; }
    }
    setSubmitting(true);
    const resp = await portalFetch("vendor-portal/submit", "POST", { form_data: formData }, token);
    if (resp.error) {
      setErrors({ _submit: resp.error.message || "Submission failed. Please try again." });
      setSubmitting(false);
      return;
    }
    setSubmitResult(resp.data?.data);
    setState("submitted");
    setSubmitting(false);
    if (autoSaveRef.current) clearInterval(autoSaveRef.current);
  };

  // ---- Check status (after submission) ----
  const [statusData, setStatusData] = useState(null);
  const checkStatus = async () => {
    const resp = await portalFetch(`vendor-portal/status/${token}`, "GET", null, token);
    if (!resp.error) setStatusData(resp.data?.data);
    setState("status");
  };

  // ======== RENDER ========

  // Loading
  if (state === "loading") {
    return (
      <div className="t2t-portal-page">
        <div className="t2t-portal-card" style={{ textAlign: "center", padding: 60 }}>
          <div style={{ fontSize: 24, marginBottom: 12 }}>{"\u23F3"}</div>
          <div style={{ fontSize: 14, color: "#64748B" }}>Validating your invitation link...</div>
        </div>
      </div>
    );
  }

  // Invalid token
  if (state === "invalid") {
    return (
      <div className="t2t-portal-page">
        <div className="t2t-portal-card" style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{"\u274C"}</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Link Expired or Invalid</h2>
          <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.6 }}>
            This vendor registration link has expired or has already been used.
            Please contact your procurement team to receive a new invitation.
          </p>
        </div>
      </div>
    );
  }

  // Submitted confirmation
  if (state === "submitted") {
    return (
      <div className="t2t-portal-page">
        <div className="t2t-portal-card" style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{"\u2705"}</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: "#16A34A" }}>Registration Submitted!</h2>
          <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.6, marginBottom: 20 }}>
            Your vendor registration has been submitted successfully. Our team will review and contact you within 3\u20135 business days.
          </p>
          {submitResult?.reference_number && (
            <div style={{ padding: 14, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "#166534" }}>Your Reference Number</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#166534", fontFamily: "monospace" }}>{submitResult.reference_number}</div>
            </div>
          )}
          <button onClick={checkStatus} style={{ background: "none", border: "none", color: "#2563EB", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            Check submission status \u2192
          </button>
        </div>
      </div>
    );
  }

  // Status check
  if (state === "status") {
    return (
      <div className="t2t-portal-page">
        <div className="t2t-portal-card" style={{ padding: 30 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Submission Status</h2>
          {statusData ? (
            <div>
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8, flex: 1, border: "1px solid #E2E8F0" }}>
                  <div style={{ fontSize: 10, color: "#64748B" }}>Reference</div>
                  <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace" }}>{statusData.reference_number || "..."}</div>
                </div>
                <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8, flex: 1, border: "1px solid #E2E8F0" }}>
                  <div style={{ fontSize: 10, color: "#64748B" }}>Status</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: statusData.status === "approved" ? "#16A34A" : statusData.status === "rejected" ? "#DC2626" : "#D97706" }}>
                    {statusData.status?.toUpperCase() || "IN PROGRESS"}
                  </div>
                </div>
              </div>
              {statusData.current_stage && (
                <div style={{ fontSize: 13, color: "#64748B" }}>Current stage: <strong>{statusData.current_stage}</strong></div>
              )}
              {statusData.vendor_number && (
                <div style={{ marginTop: 12, padding: 14, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: "#166534" }}>Your Vendor Number</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#16A34A", fontFamily: "monospace" }}>{statusData.vendor_number}</div>
                </div>
              )}
              {statusData.rejection_reason && (
                <div style={{ marginTop: 12, padding: 14, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: "#DC2626", fontWeight: 600 }}>Rejection Reason</div>
                  <div style={{ fontSize: 13, color: "#991B1B", marginTop: 4 }}>{statusData.rejection_reason}</div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#64748B" }}>Loading status...</div>
          )}
        </div>
      </div>
    );
  }

  // ======== MAIN FORM ========
  return (
    <div className="t2t-portal-page">
      {/* Header */}
      <div className="t2t-portal-header">
        <img src={process.env.PUBLIC_URL + "/logo/plotnew.png"} alt="OSIF" style={{ height: 36 }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1B3A6B" }}>Vendor Registration Portal</div>
      </div>

      <div className="t2t-portal-card">
        {/* Welcome */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>
            Complete your vendor registration
            {invitation?.company_name && <span style={{ fontWeight: 400, color: "#64748B" }}> for {invitation.company_name}</span>}
          </h2>
          <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>
            All information you enter will be reviewed by the procurement team. Fields marked <span style={{ color: "#DC2626" }}>*</span> are mandatory.
          </p>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
          {PORTAL_STEPS.map((s) => (
            <div key={s.id} style={{ flex: 1, textAlign: "center" }}>
              <div style={{
                height: 4, borderRadius: 4, marginBottom: 4,
                background: s.id < currentStep ? "#16A34A" : s.id === currentStep ? "#2563EB" : "#E2E8F0",
                transition: "0.3s",
              }} />
              <div style={{ fontSize: 9, fontWeight: 600, color: s.id <= currentStep ? "#1B3A6B" : "#94A3B8" }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Error banner */}
        {errors._submit && (
          <div style={{ padding: 12, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, marginBottom: 16, fontSize: 13, color: "#DC2626" }}>
            {errors._submit}
          </div>
        )}

        {/* Form — render domestic or international based on account group */}
        {isDomestic ? (
          <T2TVendorForm
            data={formData}
            onChange={handleFieldChange}
            stage="initiator"
            accountGroup={formData.account_group}
            errors={errors}
          />
        ) : (
          <T2TVendorFormIntl
            data={formData}
            onChange={handleFieldChange}
            stage="initiator"
            errors={errors}
          />
        )}

        {/* Footer — nav + save + submit */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 24, paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>
          <div>
            {currentStep > 1 && (
              <button className="t2t-btn t2t-btn-ghost" onClick={goBack}>{"\u2190"} Back</button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {lastSaved && (
              <span style={{ fontSize: 10, color: "#94A3B8" }}>
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
            <button className="t2t-btn t2t-btn-ghost" onClick={() => saveDraft(false)} disabled={saving}>
              {saving ? "Saving..." : "Save Draft"}
            </button>
            {currentStep < 6 ? (
              <button className="t2t-btn t2t-btn-primary" onClick={goNext}>
                Next: {PORTAL_STEPS[currentStep]?.label || "Continue"} {"\u2192"}
              </button>
            ) : (
              <button className="t2t-btn t2t-btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Submitting..." : "\u2705 Submit Registration"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "20px 0", fontSize: 11, color: "#94A3B8" }}>
        Powered by PBOPlus Consulting Services
      </div>
    </div>
  );
};

/**
 * T2TVendorInviteButton \u2014 Button for internal team to invite a vendor via portal.
 * Used on New Vendor form or request detail page.
 */
export const T2TVendorInviteButton = ({ onInviteSent }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_mobile: "", account_group: "", vendor_name: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!form.contact_name.trim() || !form.contact_email.trim() || !form.account_group || !form.vendor_name.trim()) return;
    setSending(true);
    try {
      const resp = await fetch(`${API_BASE}/vendor-portal/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      if (data.status === 200 || resp.ok) {
        setResult(data.data);
        if (onInviteSent) onInviteSent(data.data);
      }
    } catch (_e) { /* */ }
    setSending(false);
  };

  if (!open) {
    return (
      <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => setOpen(true)}>
        {"\uD83D\uDD17"} Invite Vendor via Portal
      </button>
    );
  }

  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 16, background: "#F8FAFC" }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Invite Vendor to Self-Service Portal</div>

      {result ? (
        <div style={{ padding: 12, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, fontSize: 12, color: "#166534" }}>
          {"\u2705"} Invitation sent to {form.contact_email}. Link expires in 7 days.
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div className="t2t-fg">
              <label style={{ fontSize: 11 }}>Vendor Name *</label>
              <input className="t2t-fc" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} placeholder="Company name" />
            </div>
            <div className="t2t-fg">
              <label style={{ fontSize: 11 }}>Account Group *</label>
              <select className="t2t-fc" value={form.account_group} onChange={(e) => setForm({ ...form, account_group: e.target.value })}>
                <option value="">Select...</option>
                <option value="Z101">Z101 \u2014 Domestic Regular</option>
                <option value="Z102">Z102 \u2014 Domestic Simplified</option>
                <option value="Z104">Z104 \u2014 Domestic Service</option>
                <option value="Z201">Z201 \u2014 International Regular</option>
                <option value="Z202">Z202 \u2014 International Simplified</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div className="t2t-fg">
              <label style={{ fontSize: 11 }}>Contact Name *</label>
              <input className="t2t-fc" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div className="t2t-fg">
              <label style={{ fontSize: 11 }}>Contact Email *</label>
              <input className="t2t-fc" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div className="t2t-fg">
              <label style={{ fontSize: 11 }}>Mobile (WhatsApp)</label>
              <input className="t2t-fc" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} placeholder="+91..." />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={handleSend} disabled={sending}>{sending ? "Sending..." : "Send Invitation"}</button>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
};

T2TVendorInviteButton.propTypes = { onInviteSent: PropTypes.func };

export default T2TVendorPortal;
